import { Flex, Icon, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import iconCalendar from '~/assets/svgs/icon-calendar.svg?react'
import IconCheck from '~/assets/svgs/icon-check.svg?react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DateTimeRangeModal } from '~/components/shared/DateTimeRangeModal'
import { DropdownActionButton } from '~/components/shared/Dropdown/DropdownActionButton'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { DateRange } from '~/types/Shared'
import { hasValue } from '~/utils/common'

import { DropdownMangoButton } from './Dropdown/DropdownMangoButton'
import { MenuPlacement } from './Dropdown/Menu'
import { MenuListItem } from './Dropdown/MenuItem'

const { Tooltip: TooltipColors } = themeColorSectionKeys.shared

enum DateDropdownValue {
  Today = 'Today',
  Yesterday = 'Yesterday',
  Last7Days = 'Last 7 Days',
  Last14Days = 'Last 14 Days',
  Last30Days = 'Last 30 Days',
  ThisWeek = 'This Week',
  LastWeek = 'Last Week',
  ThisMonth = 'This Month',
  LastMonth = 'Last Month',
  CustomRange = 'Custom Range',
  Last60Days = 'Last 60 Days',
  Last90Days = 'Last 90 Days',
}

type DateRangeFactory = () => DateRange

interface DateItemData {
  label: DateDropdownValue
  getValue?: DateRangeFactory
}

interface DateRangeDropdownProps {
  dateTimeRange?: DateRange
  useQuickActionButton?: boolean
  onValueChange?: (dateRange: DateRange) => void
  useExtendedDates?: boolean
  isHeatmap?: boolean
}

export const DateTimeRangeDropdown = ({
  dateTimeRange: externalDateRange,
  useQuickActionButton,
  onValueChange,
  useExtendedDates = false,
  isHeatmap = false,
}: DateRangeDropdownProps) => {
  const dateItems: DateItemData[] = [
    ...(isHeatmap
      ? []
      : [
          {
            label: DateDropdownValue.Today,
            getValue: DateRange.today,
          },
          {
            label: DateDropdownValue.Yesterday,
            getValue: DateRange.yesterday,
          },
        ]),
    {
      label: DateDropdownValue.Last7Days,
      getValue: DateRange.last7Days,
    },
    {
      label: DateDropdownValue.Last14Days,
      getValue: DateRange.last14Days,
    },
    {
      label: DateDropdownValue.Last30Days,
      getValue: DateRange.last30Days,
    },
    ...(useExtendedDates
      ? [
          {
            label: DateDropdownValue.Last60Days,
            getValue: DateRange.last60Days,
          },
          {
            label: DateDropdownValue.Last90Days,
            getValue: DateRange.last90Days,
          },
        ]
      : []),
    {
      label: DateDropdownValue.CustomRange,
    },
  ]

  const dateRangeIndices = dateItems.reduce<Record<string, number>>(
    (accum, item, index) => {
      accum[item.label] = index
      return accum
    },
    {},
  )

  // Debug helper function
  const debugDateRangeComparison = (
    name: string,
    dateRange: DateRange,
    func: () => DateRange,
  ) => {
    const generatedRange = func()
    const isEqual = DateRange.areEqual(generatedRange, dateRange)
    return isEqual
  }

  const getIndexFromDateRange = (dateRange: DateRange) => {
    const isLast30Days = debugDateRangeComparison(
      'Last30Days',
      dateRange,
      DateRange.last30Days,
    )
    if (isLast30Days) {
      return dateRangeIndices[DateDropdownValue.Last30Days]
    }

    const isToday = debugDateRangeComparison(
      'Today',
      dateRange,
      DateRange.today,
    )
    if (isToday) {
      return dateRangeIndices[DateDropdownValue.Today]
    }

    const isYesterday = debugDateRangeComparison(
      'Yesterday',
      dateRange,
      DateRange.yesterday,
    )
    if (isYesterday) {
      return dateRangeIndices[DateDropdownValue.Yesterday]
    }

    const isLast7Days = debugDateRangeComparison(
      'Last7Days',
      dateRange,
      DateRange.last7Days,
    )
    if (isLast7Days) {
      return dateRangeIndices[DateDropdownValue.Last7Days]
    }

    const isLast14Days = debugDateRangeComparison(
      'Last14Days',
      dateRange,
      DateRange.last14Days,
    )
    if (isLast14Days) {
      return dateRangeIndices[DateDropdownValue.Last14Days]
    }

    const isLast60Days = debugDateRangeComparison(
      'Last60Days',
      dateRange,
      DateRange.last60Days,
    )
    if (isLast60Days) {
      return dateRangeIndices[DateDropdownValue.Last60Days]
    }

    const isLast90Days = debugDateRangeComparison(
      'Last90Days',
      dateRange,
      DateRange.last90Days,
    )
    if (isLast90Days) {
      return dateRangeIndices[DateDropdownValue.Last90Days]
    }

    return dateRangeIndices[DateDropdownValue.CustomRange]
  }

  const [selectedDateItemIndex, setSelectedDateItemIndex] = useState(0)
  const [dateRange, setDateRange] = useState<DateRange>(
    externalDateRange ?? DateRange.empty,
  )

  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false)

  const dateItemLabels = useMemo(() => {
    return dateItems.map((item, index) => {
      const content =
        index === selectedDateItemIndex ? (
          <MenuListItem
            primaryText={item?.label}
            rightContent={<Icon as={IconCheck} />}
          />
        ) : (
          item?.label
        )
      return {
        value: item?.label?.toString(),
        content,
        buttonLabel: item?.label,
      }
    })
  }, [selectedDateItemIndex, dateItems])

  const handleDateItemSelected = (
    value: string,
    index: number,
    openModal = true,
  ) => {
    const isCustomRange =
      dateItems?.[index]?.label === DateDropdownValue.CustomRange

    if (isCustomRange) {
      if (openModal) {
        setDateRangeModalOpen(true)
        return
      }

      setSelectedDateItemIndex(index)

      if (externalDateRange) {
        setDateRange(externalDateRange)
        onValueChange?.(externalDateRange)
      }

      return
    }

    setSelectedDateItemIndex(index)
    if (!dateItems?.[index]) {
      return
    }

    const { getValue } = dateItems[index]
    if (!getValue) {
      return
    }

    const range = getValue()
    setDateRange(range)
    onValueChange?.(range)
  }

  //set selected date item index based on external date range
  useEffect(() => {
    if (!externalDateRange) return

    const dateRangeHasValue = hasValue(dateRange)
    const areDateRangesEqual = DateRange.areEqual(dateRange, externalDateRange)
    const targetIndex = getIndexFromDateRange(externalDateRange)
    const areIndicesEqual = selectedDateItemIndex === targetIndex
    const shouldHandleSelection =
      dateRangeHasValue && (!areDateRangesEqual || !areIndicesEqual)

    if (shouldHandleSelection) {
      handleDateItemSelected(
        DateDropdownValue.CustomRange.toString(),
        targetIndex ?? 0,
        false,
      )
      return
    }
  }, [externalDateRange])

  //on mount, set selected date item index if external date range is empty
  useEffect(() => {
    const needsInitialSet =
      (!hasValue(externalDateRange) ||
        DateRange.areEqual(externalDateRange, DateRange.empty)) &&
      DateRange.areEqual(dateRange, DateRange.empty)

    if (!needsInitialSet) {
      return
    }

    handleDateItemSelected(
      DateDropdownValue.Today.toString(),
      dateRangeIndices[DateDropdownValue.Today],
    )
  }, [])

  const handleDateRangeModalClose = () => {
    setDateRangeModalOpen(false)
  }

  const handleDateRangeModalSet = (dateTimeRange: DateRange) => {
    handleDateRangeModalClose()
    setDateRange(dateTimeRange)

    setSelectedDateItemIndex(dateRangeIndices[DateDropdownValue.CustomRange])
    onValueChange?.(dateTimeRange)
  }

  const handleDateRangeModalClear = () => {
    //signal modal data is cleared
    setDateRange({ ...DateRange.empty })
  }

  const tooltipLabel: string | React.ReactNode = useMemo(() => {
    const selectedItem = dateItemLabels[selectedDateItemIndex]

    const beforeText = hasValue(dateRange.after)
      ? `After: ${dateRange.after?.toLocaleString()}`
      : undefined

    const afterText = hasValue(dateRange.before)
      ? `Before: ${dateRange.before?.toLocaleString()}`
      : undefined

    if (!selectedItem) {
      return (
        <Text color={TooltipColors.FontColor} textAlign="center">
          Select Date Range
        </Text>
      )
    }

    return (
      <>
        {useQuickActionButton && (
          <Text
            color={TooltipColors.FontColor}
            textAlign="center"
            textStyle={textStyleKeys.Body2_Semibold}
          >
            {selectedItem.buttonLabel}
          </Text>
        )}
        {beforeText && (
          <Text color={TooltipColors.FontColor} textAlign="center">
            {beforeText}
          </Text>
        )}
        {afterText && (
          <Text color={TooltipColors.FontColor} textAlign="center">
            {afterText}
          </Text>
        )}
      </>
    )
  }, [
    dateItemLabels,
    selectedDateItemIndex,
    dateRange.before,
    dateRange.after,
    useQuickActionButton,
  ])

  return (
    <Flex position="relative">
      {useQuickActionButton ? (
        <DropdownActionButton
          variant={QuickActionButtonVariants.Default_200}
          items={dateItemLabels}
          selectedIndex={selectedDateItemIndex}
          onSelect={handleDateItemSelected}
          icon={iconCalendar}
          tooltipText={tooltipLabel}
          menuOptions={{
            width: '18.75rem',
            maxHeight: '35vh',
            placement: MenuPlacement.BottomRight,
          }}
        />
      ) : (
        <DropdownMangoButton
          variant={MangoButtonVariant.Default_200}
          items={dateItemLabels}
          selectedIndex={selectedDateItemIndex}
          onSelect={handleDateItemSelected}
          placeholder="Select Date Range"
          tooltipLabel={tooltipLabel}
          leftIcon={iconCalendar}
          menuOptions={{
            width: '18.75rem',
            maxHeight: '35vh',
            placement: MenuPlacement.BottomRight,
          }}
        />
      )}
      <DateTimeRangeModal
        isOpen={dateRangeModalOpen}
        dateTimeRange={dateRange}
        onClear={handleDateRangeModalClear}
        onClose={handleDateRangeModalClose}
        onSet={handleDateRangeModalSet}
      />
    </Flex>
  )
}
